<template>
  <div>
    <b-toast title="Pass Saved" static no-auto-hide>
      Successfully saved pass
    </b-toast>

    <div v-if="passGroupDetails">
      <h3>Setup Passholders</h3>
      <p>
        Please upload your name(s) and photo(s) for your 2026 {{ organisationName }} Pass(es).
      </p>
      <p>
        Please upload photos without sunglasses, hats, helmets, goggles, etc.
      </p>

      <br />

      <b-alert :show="this.infoMessage" style="max-width: 700px">
        <b>Pass Setup</b>
        <br />

        <p>
          Passes have been setup and are now awaiting printing. Passes cannot be
          changed after they have been setup. If you have made any error during
          setup, please contact us at 03 365 6530 or explore@{{ organisationEmailDomain }}
        </p>
      </b-alert>
      <b-row>
        <div
          v-for="(passholder, index) in passGroupDetails"
          :key="index"
          class="passholder passholder-details"
        >
          <div>
            <span class="pass-type">{{ passholder.passType }}</span>
            <br />

            <img
              src="/static/placeholder-image.png"
              class="placeholder-image"
              v-if="!passholder.imageUrl"
            />

            <div v-else>
              <img
                :src="passholder.imageUrl"
                class="uploaded-image"
                v-if="!passholder.showCropper"
              />

              <vue-cropper
                ref="cropper"
                v-else
                viewMode="1"
                :src="passholder.imageUrl"
                class="cropper"
                :aspectRatio="0.76"
              />
            </div>

            <br />
            <b-btn
              variant="secondary"
              size="sm"
              @click="uploadPhotos(index, passholder)"
              :disabled="infoMessage"
              >{{
                passholder.showCropper ? "Crop Photo" : "Upload Photo"
              }}</b-btn
            >
            <input
              type="file"
              style="display: none"
              ref="image"
              accept="image/png,image/jpeg"
              @change="onImageChange($event, passholder)"
            />
            <br />
            <b-form-input
              v-model="passholder.name"
              placeholder="Pass holder name"
              type="text"
              class="form-field pass-holder-name"
              :disabled="infoMessage"
            ></b-form-input>
            <b-form-checkbox
              @change="setNewMainPassholder(index)"
              name="check-button"
              switch
              class="main-passholder"
              :checked="mainPassholderIndex === index"
              :disabled="infoMessage || mainPassholderIndex === index"
            >
              Main Passholder
            </b-form-checkbox>

            <div v-if="shouldShowAutocomplete">
              Or

              <b-form-select
                @change="setPassholderFromLinkedUser(passholder)"
                v-model="passholder.linkedUser"
                :options="linkedUsers"
              >
              </b-form-select>
            </div>
          </div>
        </div>
      </b-row>

      <b-alert :show="error" variant="danger" class="pass-setup-error">
        {{ error }}
      </b-alert>

      <b-btn
        variant="secondary"
        class="save-button"
        @click="saveGroup"
        :disabled="infoMessage"
      >
        <b-spinner v-if="isLoading"></b-spinner>
        <span v-else>Save</span>
      </b-btn>
    </div>
    <div class="pass-group-spinner" v-else><b-spinner /></div>
  </div>
</template>

<script>
import api from "../api";
import Spinner from "../components/common/Spinner";
import VueCropper from "vue-cropperjs";
import config from "../constants";

export default {
  components: {
    Spinner,
    VueCropper,
  },
  data() {
    return {
      organisationId: api.getUserOrganisationId(),
      passGroupDetails: null,
      mainPassholderIndex: 0,
      componentKey: 0,
      isLoading: false,
      infoMessage: false,
      error: null,
      linkedUsers: [
        {
          value: null,
          text: "Autofill from previous upload",
          disabled: true,
        },
      ],
    };
  },
  mounted() {
    this.getPassGroupDetailsFromToken();
    this.getLinkedUsers();
  },
  methods: {
    async getPassGroupDetailsFromToken() {
      const token = this.$route.params.token;
      const passGroupDetails = await api.getPassGroupDetailsFromToken(token);

      this.passGroupDetails = passGroupDetails.map((passholder) => ({
        ...passholder,
        imageUrl: passholder.image
          ? `${config.profilePhotosUrl}/${passholder.image}`
          : null,
        linkedUser: null,
        showCropper: false,
      }));

      if (
        this.passGroupDetails.every(
          (passholder) => passholder.name && passholder.imageUrl
        )
      ) {
        this.infoMessage = true;
      }
    },
    blobToDataURL(blob, callback) {
      var a = new FileReader();
      a.onload = function (e) {
        callback(e.target.result);
      };
      a.readAsDataURL(blob);
    },
    uploadPhotos(index, passholder) {
      if (passholder.showCropper) {
        this.$refs.cropper[0].getCroppedCanvas().toBlob(
          async (blob) => {
            this.$set(passholder, "imageFile", blob);
            this.blobToDataURL(blob, (dataUrl) => {
              console.log("I made it here");
              this.$set(passholder, "imageUrl", dataUrl);
              passholder.showCropper = false;
            });
          },
          "image/jpeg",
          0.75
        );
      } else {
        this.$refs.image[index].click();
      }
    },
    onImageChange(event, passholder) {
      const files = event.target.files;
      const imageName = files[0].name;

      const fileReader = new FileReader();

      fileReader.readAsDataURL(files[0]);
      fileReader.addEventListener("load", () => {
        passholder.imageUrl = fileReader.result;
        passholder.imageFile = files[0];
        passholder.showCropper = true;
      });
    },
    async saveGroup() {
      if (this.passNotFilledIn()) {
        this.error =
          "Please make sure all passholder name and images are setup.";
        return;
      }

      this.error = null;

      this.isLoading = true;
      const data = new FormData();

      const passGroupDetails = this.passGroupDetails.map((passholder) => ({
        name: passholder.name,
      }));

      data.append("passholders", JSON.stringify(passGroupDetails));

      for (const passholder of this.passGroupDetails) {
        if (passholder.imageFile) {
          data.append("images", passholder.imageFile);
        }
      }

      data.append("mainPassholderIndex", this.mainPassholderIndex);

      try {
        await api.saveGroup(this.$route.params.token, data);

        this.isLoading = false;

        this.$bvToast.toast("Pass Setup Successful", {
          title: "Pass Setup Successful",
          variant: "success",
          solid: true,
        });

        this.infoMessage = true;
      } catch (e) {
        this.error = "Unexpected error during pass setup";
      }
    },
    setNewMainPassholder(index) {
      this.mainPassholderIndex = index;
    },
    passNotFilledIn() {
      const result = this.passGroupDetails.find(
        (details) => !details.name || !details.imageFile || !details.imageUrl
      );
      return !!result;
    },
    async getLinkedUsers() {
      const token = this.$route.params.token;

      const linkedUsers = await api.getLinkedUsers(token);

      this.linkedUsers = [
        ...this.linkedUsers,
        ...linkedUsers.map((linkedUser) => ({
          value: linkedUser,
          text: linkedUser.name,
        })),
      ];
    },
    async setPassholderFromLinkedUser(passHolder) {
      passHolder.name = passHolder.linkedUser.name;

      if (passHolder.linkedUser.image) {
        passHolder.imageUrl = `${config.profilePhotosUrl}/${passHolder.linkedUser.image}`;
        let imageFile = await fetch(passHolder.imageUrl).then((r) => r.blob());
        passHolder.imageFile = imageFile;
      }

      passHolder.linkedUser = null;
    },
  },
  computed: {
    shouldShowAutocomplete() {
      return !this.infoMessage && this.linkedUsers.length > 1;
    },
    organisationName() {
      console.log(this.organisationId)
      return this.organisationId == 306 ? "Mt Cheeseman" : "Chill";
    },
    organisationEmailDomain() {
      return this.organisationId == 306 ? "chillout.nz" : "chillout.nz";
    },
  },
};
</script>

<style lang="css" scoped>
.pass-group-spinner {
  display: flex;
  align-content: center;
  justify-content: center;
}

.passholder-details {
  margin-left: 10px;
  text-align: center;
  margin-right: 20px;
  cursor: pointer;
}

.placeholder-image {
  margin-bottom: 10px;
  height: 200px;
}

.uploaded-image {
  margin-bottom: 10px;
  height: 200px;
}

.pass-holder-name {
  margin-top: 10px;
}

.pass-type {
  font-weight: bold;
}

.cropper {
  max-height: 200px;
}

.save-button {
  margin-top: 30px;
  margin-left: -5px;
}

.passholder {
  padding: 20px;
  border: 1px solid rgb(230, 230, 230);
  border-radius: 5px;
}

.main-passholder-title {
  visibility: visible !important;
}

.main-passholder {
  margin-top: 10px;
}

.pass-setup-error {
  margin-top: 30px;
  max-width: 700px;
}
</style>



// WEBPACK FOOTER //
// src/components/PassGroupSetup.vue